// Detalle de factura en overlay responsive (Modal desktop / Drawer móvil).
// Muestra todos los datos del CobroClienteResponse SIN navegar; reemplaza a
// CobroDetailPage en el flujo de listado (fuente de verdad de campos y
// acciones).
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Space, Typography, theme } from 'antd';
import { DetalleOverlay } from '../../../components/DetalleOverlay';
import { CampoInfo, CamposInfoGrid, MontoDestacado } from '../../../components/CampoInfo';
import { EmptyState } from '../../../components/EmptyState';
import { StatusTag } from '../../../components/StatusTag';
import { FacturaStatusTag } from './FacturaStatusTag';
import { getFacturaActionMenu, getFacturaActionPolicy } from '../lib/factura-table-helpers';
import { AnularCobroModal } from './AnularCobroModal';
import { ReemitirCobroModal } from './ReemitirCobroModal';
import { useCobro } from '../api/useCobros';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { DATE_FORMAT, parseDateOnly } from '../../../lib/date';
import dayjs from 'dayjs';
import type { CobroClienteResponse } from '@aid/shared';
import {
  INVOICE_ACTION,
  INVOICE_TYPE,
  Permission,
  businessDateInTimeZone,
  formatCurrency,
} from '@aid/shared';

const { Text } = Typography;

interface CobroDetailModalProps {
  id: number | null;
  onClose: () => void;
}

function SeccionHeader({ children }: { children: string }) {
  const { token } = theme.useToken();
  return (
    <Text
      type="secondary"
      style={{
        display: 'block',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
        marginBottom: 12,
        color: token.colorTextSecondary,
      }}
    >
      {children}
    </Text>
  );
}

function Seccion({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();
  return (
    <section
      style={{
        marginTop: 20,
        paddingTop: 16,
        borderTop: `1px solid ${token.colorSplit}`,
      }}
    >
      {children}
    </section>
  );
}

function ClienteIdentidad({ cobro, status }: { cobro: CobroClienteResponse; status: ReactNode }) {
  const t = useT();
  const { token } = theme.useToken();
  const clienteId = cobro.cliente?.id ?? cobro.idCliente;
  const nombre = cobro.cliente?.nombre ?? `#${cobro.idCliente}`;
  const codigo = cobro.cliente?.ip ?? (cobro.cliente ? `#${cobro.cliente.id}` : null);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'flex-start',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <Text
          style={{
            display: 'block',
            fontSize: 22,
            fontWeight: 600,
            lineHeight: 1.3,
            color: token.colorText,
            overflowWrap: 'break-word',
          }}
        >
          {nombre}
        </Text>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: 4,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
            {codigo ?? '—'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
            ·
          </Text>
          <Link
            to={`/clientes/${clienteId}`}
            style={{ fontSize: 12, lineHeight: 1.4, color: token.colorPrimary }}
          >
            {t('components.verPerfil')}
          </Link>
        </div>
      </div>
      <div style={{ flexShrink: 0, paddingTop: 2 }}>{status}</div>
    </div>
  );
}

function formatDate(value: Date | string | null | undefined): string | null {
  // Date-only contract: legacy ISO-midnight values recover the calendar date.
  return parseDateOnly(value)?.format(DATE_FORMAT) ?? null;
}

function formatApplicationState(estado: string, t: ReturnType<typeof useT>): string {
  return estado === 'ACTIVA'
    ? t('pages.cobros.aplicacionActiva')
    : t('pages.cobros.aplicacionReversada');
}

export function CobroDetailModal({ id, onClose }: CobroDetailModalProps) {
  const t = useT();
  const { data: cobro, isLoading, error, refetch } = useCobro(id ?? 0);
  // Re-issue reuses the annul permission (no new permission entry); the
  // display-only gate stays behind it, mirroring the facturas table.
  const canAnular = useHasPermission(Permission.COBROS_ANULAR);
  const canReissue = useHasPermission(Permission.COBROS_ANULAR);
  const { token } = theme.useToken();

  const [anularModalOpen, setAnularModalOpen] = useState(false);
  const [reemitirOpen, setReemitirOpen] = useState(false);
  const actionPolicy = cobro ? getFacturaActionPolicy(cobro) : null;
  // Display-only re-issue gate (spec IR-W1 FE); the backend re-checks the
  // window/package authoritatively on preview and on the atomic command.
  const reissueGate = cobro
    ? getFacturaActionMenu(cobro, { fechaBase: businessDateInTimeZone() }).find(
        (item) => item.key === INVOICE_ACTION.REISSUE,
      )
    : null;

  const footer = (
    <Space wrap>
      {canAnular && cobro && actionPolicy?.annul.visible && actionPolicy?.annul.enabled && (
        <Button
          danger
          onClick={() => setAnularModalOpen(true)}
        >
          {t('pages.cobros.anular')}
        </Button>
      )}
      {canReissue && cobro && reissueGate?.visible && (
        <Button onClick={() => setReemitirOpen(true)}>{t('pages.cobros.reemitir')}</Button>
      )}
      <Button onClick={onClose}>{t('common.close')}</Button>
      {/* R2: edit is no longer offered; invoice corrections only use reissue or annulment. */}
    </Space>
  );

  return (
    <>
      <DetalleOverlay
        open={id !== null}
        onClose={onClose}
        title={id !== null ? t('pages.cobros.detailId', { id }) : undefined}
        footer={footer}
        loading={isLoading}
      >
        {error || !cobro ? (
          <EmptyState
            title={t('pages.cobros.notFound')}
            description={t('pages.cobros.notFoundDetail')}
          />
        ) : (
          <div>
            <ClienteIdentidad
              cobro={cobro}
              status={
                <Space wrap>
                  <FacturaStatusTag factura={cobro} />
                  <StatusTag
                    tone={cobro.tipoFactura === INVOICE_TYPE.EXTRAORDINARIA ? 'info' : 'neutral'}
                  >
                    {cobro.tipoFactura === INVOICE_TYPE.EXTRAORDINARIA
                      ? t('pages.cobros.tipoFacturaExtraordinaria')
                      : t('pages.cobros.tipoFacturaPeriodica')}
                  </StatusTag>
                </Space>
              }
            />

            <Seccion>
              <SeccionHeader>{t('pages.cobros.finanzas')}</SeccionHeader>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 16,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 4 }}
                  >
                    {t('pages.cobros.montoFactura')}
                  </Text>
                  <div
                    style={{
                      background: token.colorFillTertiary,
                      border: `1px solid ${token.colorBorderSecondary}`,
                      borderRadius: token.borderRadiusLG,
                      padding: '10px 14px',
                    }}
                  >
                    <MontoDestacado
                      value={formatCurrency(cobro.valor)}
                      color={Number(cobro.valor) > 0 ? token.colorSuccess : undefined}
                    />
                  </div>
                </div>
                {cobro.balance ? (
                  <div style={{ minWidth: 0 }}>
                    <Text
                      type="secondary"
                      style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 4 }}
                    >
                      {t('pages.cobros.balanceSaldo')}
                    </Text>
                    <Text
                      style={{
                        display: 'block',
                        padding: '10px 0',
                        fontSize: 20,
                        fontWeight: 700,
                        lineHeight: 1.3,
                        fontVariantNumeric: 'tabular-nums',
                        color:
                          Number(cobro.balance.saldoPendiente) > 0
                            ? token.colorError
                            : token.colorSuccess,
                      }}
                    >
                      {formatCurrency(cobro.balance.saldoPendiente)}
                    </Text>
                  </div>
                ) : null}
              </div>
            </Seccion>

            <Seccion>
              <SeccionHeader>{t('pages.cobros.periodoFechas')}</SeccionHeader>
              <CamposInfoGrid>
                {cobro.fechaInicio && cobro.fechaFin ? (
                  <CampoInfo
                    label={t('pages.clientes.modal.periodo')}
                    value={[formatDate(cobro.fechaInicio), formatDate(cobro.fechaFin)]
                      .filter(Boolean)
                      .join(' – ')}
                  />
                ) : null}
                <CampoInfo
                  label={t('pages.cobros.fechaCobro')}
                  value={formatDate(cobro.fechaCreacion)}
                />
                <CampoInfo
                  label={t('pages.cobros.aplicarSaldoAFavor')}
                  value={
                    cobro.aplicarSaldoAFavor !== false
                      ? t('pages.cobros.aplicarSaldoAFavorYes')
                      : t('pages.cobros.aplicarSaldoAFavorNo')
                  }
                />
                {cobro.generadoPor ? (
                  <CampoInfo
                    label={t('pages.cobros.generadoPor')}
                    value={
                      cobro.generadoPor === 'MANUAL'
                        ? t('pages.cobros.generadoPorManual')
                        : cobro.generadoPor === 'CRON'
                          ? t('pages.cobros.generadoPorCron')
                          : t('pages.cobros.generadoPorAdelanto')
                    }
                  />
                ) : null}
              </CamposInfoGrid>
            </Seccion>

            {cobro.concepto ? (
              <Seccion>
                <SeccionHeader>{t('pages.cobros.concepto')}</SeccionHeader>
                <Text style={{ display: 'block', lineHeight: 1.5, overflowWrap: 'break-word' }}>
                  {cobro.concepto}
                </Text>
              </Seccion>
            ) : null}

            {cobro.balance || (cobro.aplicaciones?.length ?? 0) > 0 ? (
              <Seccion>
                <SeccionHeader>{t('pages.cobros.balance')}</SeccionHeader>
                {cobro.balance ? (
                  <CamposInfoGrid>
                    {Math.abs(Number(cobro.balance.gross) - Number(cobro.valor)) > 0.005 ? (
                      <CampoInfo
                        label={t('pages.cobros.balanceGross')}
                        value={formatCurrency(cobro.balance.gross)}
                      />
                    ) : null}
                    <CampoInfo
                      label={t('pages.cobros.balanceAjustes')}
                      value={formatCurrency(cobro.balance.ajustesAplicados)}
                    />
                    <CampoInfo
                      label={t('pages.cobros.balanceAplicado')}
                      value={formatCurrency(cobro.balance.montoAplicado)}
                    />
                  </CamposInfoGrid>
                ) : null}
                {(cobro.aplicaciones?.length ?? 0) > 0 ? (
                  <div style={{ marginTop: cobro.balance ? 16 : 0 }}>
                    <Text
                      type="secondary"
                      style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 8 }}
                    >
                      {t('pages.cobros.aplicaciones')}
                    </Text>
                    <div style={{ borderTop: `1px solid ${token.colorSplit}` }}>
                      {cobro.aplicaciones!.map((aplicacion) => {
                        const referencia =
                          aplicacion.idPago !== null
                            ? t('pages.cobros.aplicacionPago', { id: aplicacion.idPago })
                            : aplicacion.idCredito !== null
                              ? t('pages.cobros.aplicacionCredito', { id: aplicacion.idCredito })
                              : t('pages.cobros.aplicacionSinReferencia');
                        return (
                          <div
                            key={aplicacion.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 12,
                              padding: '10px 0',
                              borderBottom: `1px solid ${token.colorSplit}`,
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <Text style={{ display: 'block', fontWeight: 500 }}>
                                {referencia}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {formatApplicationState(aplicacion.estado, t)}
                              </Text>
                            </div>
                            <Text style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                              {formatCurrency(aplicacion.montoAplicado)}
                            </Text>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </Seccion>
            ) : null}

            <Seccion>
              <SeccionHeader>{t('pages.cobros.lineas')}</SeccionHeader>
              {cobro.tieneLineas && cobro.lineas && cobro.lineas.length > 0 ? (
                <div style={{ borderTop: `1px solid ${token.colorSplit}` }}>
                  {cobro.lineas.map((linea) => (
                    <div
                      key={linea.idServicio}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '10px 0',
                        borderBottom: `1px solid ${token.colorSplit}`,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <Text style={{ display: 'block', fontWeight: 500, lineHeight: 1.4 }}>
                          {linea.descripcion}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t('pages.cobros.precioUnitario')}: {formatCurrency(linea.precioUnitario)}
                        </Text>
                      </div>
                      <Text style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                        {formatCurrency(linea.subtotal)}
                      </Text>
                    </div>
                  ))}
                </div>
              ) : (
                <Text type="secondary" style={{ display: 'block', lineHeight: 1.5 }}>
                  {t('pages.cobros.lineaHistorica')}
                </Text>
              )}
            </Seccion>

            {cobro.comentario ? (
              <Seccion>
                <SeccionHeader>{t('pages.cobros.comentario')}</SeccionHeader>
                <Text style={{ display: 'block', lineHeight: 1.5, overflowWrap: 'break-word' }}>
                  {cobro.comentario}
                </Text>
              </Seccion>
            ) : null}

            <Seccion>
              <SeccionHeader>{t('pages.clientes.infoAuditoria')}</SeccionHeader>
              <CamposInfoGrid>
                <CampoInfo label={t('common.createdAt')} value={formatDate(cobro.fechaCreacion)} />
                <CampoInfo
                  label={t('common.updatedAt')}
                  value={formatDate(cobro.fechaModificacion)}
                />
                {cobro.usuarioModifica ? (
                  <CampoInfo
                    full
                    label={t('pages.clientes.modificadoPor')}
                    value={cobro.usuarioModifica}
                  />
                ) : null}
              </CamposInfoGrid>
            </Seccion>

            {cobro.motivoAnulacion ? (
              <Seccion>
                <SeccionHeader>{t('pages.clientes.modal.anulacion')}</SeccionHeader>
                <CamposInfoGrid>
                  <CampoInfo
                    full
                    label={t('pages.cobros.motivoAnulacion')}
                    value={cobro.motivoAnulacion}
                  />
                  <CampoInfo
                    full
                    label={t('pages.cobros.fechaAnulacion')}
                    value={
                      cobro.fechaAnulacion
                        ? dayjs(cobro.fechaAnulacion).format(`${DATE_FORMAT} HH:mm`)
                        : undefined
                    }
                  />
                </CamposInfoGrid>
              </Seccion>
            ) : null}
          </div>
        )}
      </DetalleOverlay>
      <AnularCobroModal
        open={anularModalOpen}
        cobroId={cobro?.id ?? 0}
        onClose={() => setAnularModalOpen(false)}
        onSuccess={() => refetch()}
      />
      {reemitirOpen && cobro && (
        <ReemitirCobroModal
          open
          cobroId={cobro.id}
          idCliente={cobro.idCliente}
          onClose={() => setReemitirOpen(false)}
          onSuccess={() => refetch()}
        />
      )}
    </>
  );
}
